import React, { useState, useRef, useEffect } from "react";
import axios from "axios";

interface Message {
  role: "user" | "assistant";
  content: string;
  isStreaming?: boolean; 
}

const DEFAULT_GREETING_MESSAGE =
  "Hi there! Welcome to our bookstore. How can I assist you today ?";

// --- THE FIX: Typing effect without the "undefined" bug ---
const StreamingText: React.FC<{ text: string }> = ({ text }) => {
  const [displayedText, setDisplayedText] = useState("");
  
  useEffect(() => {
    setDisplayedText(""); 
    if (!text) return;

    // Filter out empty or null items to prevent "undefined" strings
    const words = text.split(" ").filter(w => !!w);
    let i = 0;
    
    const interval = setInterval(() => {
      if (i < words.length) {
        const nextWord = words[i];
        setDisplayedText((prev) => prev === "" ? nextWord : prev + " " + nextWord);
        i++;
      } else {
        clearInterval(interval);
      }
    }, 40); 

    return () => clearInterval(interval);
  }, [text]);

  return <span>{displayedText}</span>;
};

// --- THE CHAT: Uses your original CSS classes ---
const Chat: React.FC<{ clearTrigger: number }> = ({ clearTrigger }) => {
  const [messages, setMessages] = useState<Message[]>(() => [
    { role: "assistant", content: DEFAULT_GREETING_MESSAGE },
  ]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(
    () =>
      setMessages([
        { role: "assistant", content: DEFAULT_GREETING_MESSAGE },
      ]),
    [clearTrigger]
  );

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  const sendMessage = async () => {
    const trimmedInput = input.trim();
    if (!trimmedInput || isTyping) return;

    const userMsg: Message = { role: "user", content: trimmedInput };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setIsTyping(true);

    try {
      const res = await axios.post("http://localhost:3003/chat", {
        message: trimmedInput,
        // Don't send the default greeting back to the server as chat history.
        history: [...messages, userMsg].filter((_, idx) => idx !== 0),
      });

      const rawReply = res.data.reply;

      if (rawReply) {
        const aiMsg: Message = { 
          role: "assistant", 
          content: String(rawReply), 
          isStreaming: true 
        };
        setMessages((prev) => [...prev, aiMsg]);
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev, 
        { role: "assistant", content: "⚠️ Error connecting to service." }
      ]);
    } finally {
      setIsTyping(false);
      // Input is disabled while typing; refocus after it’s enabled again.
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  return (
    <div className="chat-main-layout">
      {/* Container for messages */}
      <div className="chat-messages-scroll">
        {messages.map((msg, idx) => (
          <div key={idx} className={`msg-row ${msg.role}`}>
            {/* The bubble uses your existing CSS styles */}
            <div className="msg-bubble">
              {msg.role === "assistant" && msg.isStreaming ? (
                <StreamingText text={msg.content} />
              ) : (
                msg.content
              )}
            </div>
          </div>
        ))}
        {isTyping && <div className="typing-indicator">AI is thinking...</div>}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Input area uses your original CSS classes */}
      <div className="chat-input-sticky">
        <input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && sendMessage()}
          placeholder="Type your message..."
          disabled={isTyping}
          autoFocus
        />
        <button onClick={sendMessage} disabled={isTyping || !input.trim()}>
          Send
        </button>
      </div>
    </div>
  );
};

export default Chat;